import { useEffect, useState, useCallback } from "react";
import { Sparkles, RefreshCw, MapPin } from "lucide-react";
import { api } from "../lib/api";
import { escapeHtml } from "../lib/sanitize";
import PeriodBadge from "./PeriodBadge";

function renderMd(md) {
  if (!md) return null;
  const lines = md.split(/\r?\n/);
  const inline = (t) =>
    escapeHtml(t)
      .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-[#0a0a0a]">$1</strong>')
      .replace(/\*([^*]+)\*/g, "<em>$1</em>");
  const out = [];
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^---+$/.test(line)) continue;
    if (/^#{2,4}\s+/.test(line)) {
      out.push(
        <h4 key={`h-${out.length}`} className="bm-headline text-lg mt-4 first:mt-0">
          {line.replace(/^#{2,4}\s+/, "")}
        </h4>
      );
    } else if (/^[-*]\s+/.test(line)) {
      out.push(
        <div key={`b-${out.length}`} className="flex gap-2 text-sm text-neutral-800 my-1.5 leading-relaxed">
          <span className="text-[#c72c2c] mt-[2px]">▸</span>
          <span dangerouslySetInnerHTML={{ __html: inline(line.replace(/^[-*]\s+/, "")) }} />
        </div>
      );
    } else if (line.trim() === "") {
      // spacer
    } else {
      out.push(
        <p key={`p-${out.length}`} className="text-sm text-neutral-800 my-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(line) }} />
      );
    }
  }
  return out;
}

function StoreChip({ label, tone }) {
  const cls =
    tone === "open"
      ? "text-emerald-800 border-emerald-700/40 bg-emerald-50/70"
      : tone === "pair"
      ? "text-[#c72c2c] border-[#c72c2c]/40 bg-[#c72c2c]/5"
      : "text-neutral-600 border-neutral-400/40 bg-neutral-100/50";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 border ${cls} text-[9px] font-mono-bm uppercase tracking-wider`}>
      {label}
    </span>
  );
}

export default function TeeDropOpportunities({ range }) {
  const [state, setState] = useState({ status: "empty" });
  const [loading, setLoading] = useState(false);

  const loadLatest = useCallback(() => {
    if (!range) return;
    api
      .get("/tees/drop-opportunities/latest", { params: { start_date: range.start, end_date: range.end } })
      .then((r) => setState(r.data))
      .catch(() => {});
  }, [range?.start, range?.end]);

  useEffect(() => { loadLatest(); }, [loadLatest]);

  async function analyze() {
    if (!range) return;
    setLoading(true);
    try {
      const { data } = await api.post("/tees/drop-opportunities", null, {
        params: { start_date: range.start, end_date: range.end, refresh: true },
      });
      setState(data);
    } catch (e) {
      setState({ status: "error", error: "Analysis failed — try again." });
    } finally {
      setLoading(false);
    }
  }

  const opps = state.opportunities || [];
  const hasResult = state.status === "ok" && state.markdown;

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-6" data-testid="tee-drop-opportunities">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
        <div className="bm-eyebrow flex items-center gap-1.5">
          <MapPin size={11} /> Table 10 · Drop planner
        </div>
        <div className="bm-eyebrow text-neutral-500">{range?.label || ""}</div>
      </div>
      <h3 className="bm-headline text-3xl mb-1">Where to drop next.</h3>
      <PeriodBadge range={range} className="mb-2" />
      <p className="bm-eyebrow text-neutral-500 mb-4 max-w-[640px]">
        Claude ranks the smartest new floors for each proven tee — never running the same design in both 256 &amp; 347 (too close, same shoppers).
      </p>

      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={analyze}
          disabled={loading || !range}
          data-testid="tee-drop-analyze"
          className="bm-eyebrow px-4 py-2 border border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-[#f5f2eb] disabled:opacity-60 flex items-center gap-2"
        >
          {loading ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {loading ? "Analyzing…" : hasResult ? "Re-run analysis" : "Analyze opportunities"}
        </button>
        {state.generated_at && (
          <span className="bm-eyebrow text-neutral-500">
            Last run · {new Date(state.generated_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </span>
        )}
      </div>

      {state.status === "error" && (
        <div className="text-sm text-[#c72c2c]" data-testid="tee-drop-error">{state.error || "Analysis failed."}</div>
      )}
      {!hasResult && state.status !== "error" && !loading && (
        <div className="text-sm text-neutral-600">
          {state.status === "empty"
            ? "No standing designs have open floors in this window — every seller is already placed within the rules."
            : "Run the analysis to see the best new drops for this period."}
        </div>
      )}

      {hasResult && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-[#f5f2eb] border border-[#0a0a0a]/40 p-4" data-testid="tee-drop-analysis">
            {renderMd(state.markdown)}
          </div>
          {opps.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tee-drop-table">
                <thead>
                  <tr className="border-b border-[#0a0a0a]">
                    <th className="text-left bm-eyebrow text-neutral-600 py-2 pr-3">Design</th>
                    <th className="text-left bm-eyebrow text-neutral-600 py-2 px-3">In</th>
                    <th className="text-left bm-eyebrow text-neutral-600 py-2 px-3">Open to</th>
                    <th className="text-right bm-eyebrow text-neutral-600 py-2 pl-3">Units</th>
                  </tr>
                </thead>
                <tbody>
                  {opps.slice(0, 12).map((o, i) => (
                    <tr key={i} className="border-b border-[#0a0a0a]/15" data-testid={`tee-drop-row-${i}`}>
                      <td className="py-2 pr-3 align-middle">
                        <div className="font-medium truncate max-w-[200px]" title={o.design}>{o.design}</div>
                        {o.legs_label && <div className="bm-eyebrow text-neutral-500">{o.legs_label}</div>}
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <div className="flex flex-wrap gap-1">
                          {o.present_in.map((s) => <StoreChip key={s} label={s} tone="in" />)}
                        </div>
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <div className="flex flex-wrap gap-1">
                          {o.open_stores.map((s) => <StoreChip key={s} label={s} tone="open" />)}
                          {o.pair_choice && <StoreChip label={`${o.pair_choice.join(" or ")}`} tone="pair" />}
                        </div>
                      </td>
                      <td className="py-2 pl-3 text-right font-mono-bm font-semibold tabular-nums align-middle">
                        {o.total_units.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
